import { useLocation, useNavigate } from 'react-router-dom';
import {
  Settings2,
  Building,
  Palette,
  ShieldAlert,
  Ban,
  Webhook,
  Key,
  Database,
  FileText,
  ScrollText,
  Settings,
  Percent,
  Users,
  ClipboardList,
  CreditCard,
  ShieldCheck,
  UserCircle,
  BookOpen,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useAuth } from '@/hooks/useAuth';
import { useSubscriptionStore } from '@/stores/subscriptionStore';
import { MODULES } from '@/constants/modules';
import { ROUTES } from '@/constants/routes';

const SETTINGS_ITEMS = [
  { label: 'My Profile',      route: ROUTES.SETTINGS_PROFILE,         icon: UserCircle,  adminOnly: false, auditorVisible: false, moduleId: null },
  { label: 'General',         route: ROUTES.SETTINGS_GENERAL,         icon: Settings2,   adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Company Profile', route: ROUTES.SETTINGS_COMPANY_PROFILE, icon: Building,    adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Branding',        route: ROUTES.SETTINGS_BRANDING,        icon: Palette,     adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Fraud Rules',     route: ROUTES.SETTINGS_FRAUD_RULES,     icon: ShieldAlert, adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Blacklist',       route: ROUTES.SETTINGS_BLACKLIST,       icon: Ban,         adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Tax & Compliance',    route: ROUTES.SETTINGS_TAX,           icon: Percent,      adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Billing & Subscription', route: ROUTES.SETTINGS_BILLING,   icon: CreditCard,   adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Users & Roles',       route: ROUTES.SETTINGS_USERS,         icon: Users,        adminOnly: true,  auditorVisible: false, moduleId: null },
  // POST-LAUNCH: { label: 'Roles & Permissions', route: ROUTES.SETTINGS_ROLES, icon: ShieldCheck, adminOnly: true, auditorVisible: false, moduleId: null },
  { label: 'Audit Log',           route: ROUTES.SETTINGS_AUDIT_LOG,     icon: ClipboardList, adminOnly: true, auditorVisible: true,  moduleId: null },
  { label: 'Webhooks',            route: ROUTES.SETTINGS_WEBHOOKS,      icon: Webhook,      adminOnly: true,  auditorVisible: false, moduleId: MODULES.INTEGRATION },
  { label: 'API Keys',        route: ROUTES.SETTINGS_API_KEYS,        icon: Key,         adminOnly: true,  auditorVisible: false, moduleId: MODULES.INTEGRATION },
  { label: 'Migration',       route: ROUTES.SETTINGS_MIGRATION,       icon: Database,    adminOnly: true,  auditorVisible: false, moduleId: MODULES.INTEGRATION },
  { label: 'Certificates',    route: ROUTES.SETTINGS_CERTIFICATES,    icon: FileText,    adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'Policy Wording',  route: ROUTES.SETTINGS_WORDING,         icon: ScrollText,  adminOnly: true,  auditorVisible: false, moduleId: null },
  { label: 'MFA',             route: ROUTES.SETTINGS_MFA,             icon: ShieldCheck, adminOnly: false, auditorVisible: false, moduleId: null },
  { label: 'User Guide',     route: ROUTES.GUIDE,                    icon: BookOpen,    adminOnly: false, auditorVisible: true,  moduleId: null },
] as const;

export default function SettingsSidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isIcAdmin, role } = useAuth();
  const hasModule = useSubscriptionStore((s) => s.hasModule);

  const visibleItems = SETTINGS_ITEMS.filter((item) => {
    if (item.moduleId && !hasModule(item.moduleId)) return false;
    if (item.auditorVisible && role === 'ic-auditor') return true;
    if (item.adminOnly && !isIcAdmin()) return false;
    return true;
  });

  return (
    <div className="hidden md:flex w-[200px] flex-shrink-0 flex-col border-r border-white/10 bg-sidebar">
      <div className="flex h-16 items-center gap-2 px-4 border-b border-white/10 bg-white/[0.05]">
        <Settings className="h-4 w-4 text-white/70 shrink-0" />
        <span className="text-sm font-semibold text-white">Settings</span>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 bg-white/[0.05]">
        {visibleItems.map((item) => {
          const isActive = pathname.startsWith(item.route);
          return (
            <button
              key={item.route}
              type="button"
              onClick={() => navigate(item.route)}
              className={cn(
                'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                isActive
                  ? 'bg-white/15 text-white font-medium'
                  : 'text-white/70 hover:bg-white/10 hover:text-white',
              )}
            >
              <item.icon className="h-4 w-4 shrink-0" />
              <span className="truncate text-left">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
